import React, { useState } from "react";
import { SketchPicker } from "react-color";

import ColorCustomizer from "../ColorCustomiser/ColorCustomizer";
import "./ChartTheme.css";

const ChartTheme = ({ setChartConfig, chartConfig }) => {
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showTextPicker, setShowTextPicker] = useState(false);
  const [customThemeColors, setCustomThemeColors] = useState(chartConfig.colors || []);
  const [isCustomThemeSelected, setIsCustomThemeSelected] = useState(false);
  const [isTransparent, setIsTransparent] = useState(chartConfig?.chart?.background === "transparent");
  const [mode, setMode] = useState(chartConfig.theme.mode);

  const predefinedThemes = [
    {
      id: "palette1",
      name: "palette1",
      colors: ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0"]
    },
    {
      id: "palette2",
      name: "palette2",
      colors: ["#3f51b5", "#03a9f4", "#4caf50", "#f9ce1d", "#FF9800"]
    },
    {
      id: "palette3",
      name: "palette3",
      colors: ["#33b2df", "#546E7A", "#d4526e", "#13d8aa", "#A5978B"]
    },
    {
      id: "palette4",
      name: "palette4",
      colors: ["#4ecdc4", "#c7f464", "#81D4FA", "#546E7A", "#fd6a6a"]
    },
    {
      id: "palette5",
      name: "palette5",
      colors: ["#2b908f", "#f9a3a4", "#90ee7e", "#fa4443", "#69d2e7"]
    },
    {
      id: "palette6",
      name: "palette6",
      colors: ["#449DD1", "#F86624", "#EA3546", "#662E9B", "#C5D86D"]
    },
    {
      id: "palette7",
      name: "palette7",
      colors: ["#D7263D", "#1B998B", "#2E294E", "#F46036", "#E2C044"]
    },
    {
      id: "palette8",
      name: "palette8",
      colors: ["#662E9B", "#F86624", "#F9C80E", "#EA3546", "#43BCCD"]
    },
    {
      id: "palette9",
      name: "palette9",
      colors: ["#5C4742", "#A5978B", "#8D5B4C", "#5A2A27", "#C4BBAF"]
    },
    {
      id: "palette10",
      name: "palette10",
      colors: ["#A300D6", "#7D02EB", "#5653FE", "#2983FF", "#00B1F2"]
    }
  ];

  const handleThemeChange = (themeId) => {
    if (themeId === "custom") {
      setIsCustomThemeSelected(true);
    } else {
      setIsCustomThemeSelected(false);
      handleCustomColorsChange([]);
    }

    const newTheme = { palette: themeId };

    setChartConfig((prev) => ({
      ...prev,
      theme: newTheme
    }));
  };

  const handleBackgroundColorChange = (color) => {
    setChartConfig((prev) => ({ ...prev, chart: { ...prev.chart, background: color.hex } }));
  };

  const handleTextColorChange = (color) => {
    setChartConfig((prev) => ({ ...prev, chart: { ...prev.chart, foreColor: color.hex } }));
  };

  const handleMode = (mode) => {
    setMode(mode);

    setChartConfig((prev) => ({
      ...prev,
      theme: { ...prev.theme, mode },
      chart: {
        ...prev.chart,
        background: mode === "dark" ? "#343E59" : "#ffffff",
        foreColor: mode === "dark" ? "#ffffff" : "#000000"
      }
    }));
  };

  const handleCustomColorsChange = (newColors) => {
    setCustomThemeColors(newColors);

    if (isCustomThemeSelected) {
      setChartConfig((prev) => ({ ...prev, colors: newColors }));
    }
  };

  const handleWidthChange = (e) => {
    const newWidth = parseInt(e.target.value, 10);

    if (!isNaN(newWidth)) {
      setChartConfig((prev) => ({ ...prev, width: newWidth }));
    }
  };

  const handleHeightChange = (e) => {
    const newHeight = parseInt(e.target.value, 10);
    if (!isNaN(newHeight)) {
      setChartConfig((prev) => ({ ...prev, height: newHeight }));
    }
  };

  const handleTransparent = (e) => {
    if (e.target.checked) setChartConfig((prev) => ({ ...prev, chart: { ...prev.chart, background: "transparent" } }));

    setIsTransparent(e.target.checked);
  };

  const selectedTheme = chartConfig.theme.palette;
  const backgroundColor = chartConfig.chart.backgroundColor;
  const foreColor = chartConfig.chart.foreColor;

  return (
    <div className="chart-theme-container">
      <div className="theme-header">
        <h3 className="theme-title">Chart Theme</h3>
        <div className="mode-toggle">
          <button className={`mode-button ${mode === "light" ? "active" : ""}`} onClick={() => handleMode("light")}>
            ☀️
          </button>
          <button onClick={() => handleMode("dark")} className={`mode-button ${mode === "dark" ? "active" : ""}`}>
            🌙
          </button>
        </div>
      </div>

      <div className="section">
        <div className="section-title">Predefined Themes</div>
        <div className="theme-grid">
          {predefinedThemes.map((theme) => (
            <div
              key={theme.id}
              className={`theme-option ${selectedTheme === theme.id ? "selected" : ""}`}
              onClick={() => handleThemeChange(theme.id)}
            >
              <input
                type="radio"
                className="radio-input"
                checked={selectedTheme === theme.id}
                onChange={() => handleThemeChange(theme.id)}
              />
              <div className="theme-details">
                <span className="theme-name">{theme.name}</span>
                <div className="color-preview">
                  {theme.colors.map((color, index) => (
                    <div key={index} className="color-dot" style={{ backgroundColor: color }} />
                  ))}
                </div>
              </div>
            </div>
          ))}

          <div
            className={`theme-option ${selectedTheme === "custom" ? "selected" : ""}`}
            onClick={() => handleThemeChange("custom")}
          >
            <input
              type="radio"
              className="radio-input"
              checked={selectedTheme === "custom"}
              onChange={() => handleThemeChange("custom")}
            />
            <div className="theme-details">
              <span className="theme-name">Custom Theme</span>
              <div className="color-preview">
                {customThemeColors.map((color, index) => (
                  <div key={index} className="color-dot" style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use ColorCustomizer for Custom Colors */}
      {isCustomThemeSelected && (
        <div className="section">
          <ColorCustomizer colors={customThemeColors} onColorsChange={handleCustomColorsChange} />
        </div>
      )}

      <div className="section">
        <div className="section-title">Custom Colors</div>
        <div className="d_flex gap_3 mb_3 align_center">
          <span className="section-title sub">Background Transparent</span>
          <label className="toggle-switch">
            <input type="checkbox" checked={isTransparent} onChange={(e) => handleTransparent(e)} />
            <span className="slider"></span>
          </label>
        </div>
        <div className="color-pickers">
          <div className="color-picker-wrapper">
            <button
              className="color-picker-button"
              onClick={() => setShowBgPicker(!showBgPicker)}
              disabled={isTransparent}
            >
              <div className="color-swatch" style={{ backgroundColor }} />
              Background
            </button>
            {showBgPicker && (
              <div className="picker-popover">
                <div className="picker-cover" onClick={() => setShowBgPicker(false)} />
                <SketchPicker color={backgroundColor} onChangeComplete={handleBackgroundColorChange} />
              </div>
            )}
          </div>

          <div className="color-picker-wrapper">
            <button className="color-picker-button" onClick={() => setShowTextPicker(!showTextPicker)}>
              <div className="color-swatch" style={{ backgroundColor: foreColor }} />
              Text
            </button>
            {showTextPicker && (
              <div className="picker-popover">
                <div className="picker-cover" onClick={() => setShowTextPicker(false)} />
                <SketchPicker color={foreColor} onChangeComplete={handleTextColorChange} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-title">Chart Dimensions</div>
        <div className="dimension-inputs">
          <div className="dimension-input">
            <label>Width:</label>
            <input type="number" value={chartConfig.width || 500} onChange={handleWidthChange} min="100" max="2000" />
          </div>
          <div className="dimension-input">
            <label>Height:</label>
            <input type="number" value={chartConfig.height || 500} onChange={handleHeightChange} min="100" max="2000" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartTheme;
